import { useEffect, useRef } from "react";

import { AGENTIC_LOGIC_RULE_ENTRY_FILENAME } from "@intelligems/ig-types/functions/agentic-logic/workspace/files";
import { type RuleGraphDto } from "@intelligems/ig-types/dtos/agentic-logic/rule-graph";
import { useAgenticLogicRuleDraft } from "@src/api/agentic-logic/get-agentic-logic-rule-draft";
import { useAgenticLogicRuleScenarios } from "@src/api/agentic-logic/get-agentic-logic-rule-scenarios";
import { useSaveAgenticLogicRuleDraft } from "@src/api/agentic-logic/save-agentic-logic-rule-draft";
import { useGenerateAgenticLogicScenarios } from "@src/api/ai/generate-agentic-logic-scenarios";

/**
 * Keeps a rule's cart scenarios covering its current branches.
 *
 * The GET is a pure read — it reports "pending" but never starts generation
 * (polling a route that enqueues compounds one queue message per tick).
 * Generation runs on the DIRECT lane, AT MOST ONCE per mount, the first time
 * the set is reported uncovered: call the ai service with the saved draft
 * source, then persist the result through save-draft — whose cache
 * invalidation is what flips the query to "ready". Template rules never report
 * pending, and a rule with no draft has nothing to generate from.
 *
 * Owned by the rule workspace rather than by whatever renders the scenarios:
 * the graph is what makes the set stale, and Atlas — which offers the carts —
 * must not be the thing that writes a draft.
 */
export const useEnsureRuleScenarios = ({
  ruleId,
  graph,
}: {
  ruleId: string;
  graph: RuleGraphDto | null;
}) => {
  const scenariosQuery = useAgenticLogicRuleScenarios(
    graph !== null ? ruleId : undefined
  );
  const draftQuery = useAgenticLogicRuleDraft(ruleId);
  const generate = useGenerateAgenticLogicScenarios();
  const save = useSaveAgenticLogicRuleDraft();
  const hasRequestedRegen = useRef(false);
  const isPendingCoverage = scenariosQuery.data?.status === "pending";
  const draftSource = draftQuery.data?.draft?.source ?? null;

  useEffect(() => {
    if (!isPendingCoverage || hasRequestedRegen.current) {
      return;
    }
    if (draftSource === null || !scenariosQuery.data || graph === null) {
      return;
    }
    hasRequestedRegen.current = true;
    // Read-only context for the generator; the save below writes none.
    const storedDependencies = draftQuery.data?.draft?.dependencies ?? [];
    const authored = scenariosQuery.data.scenarios.filter(
      (scenario) => scenario.origin !== "generated"
    );
    generate.mutate(
      {
        ruleId,
        workspace: {
          entry: AGENTIC_LOGIC_RULE_ENTRY_FILENAME,
          files: { [AGENTIC_LOGIC_RULE_ENTRY_FILENAME]: draftSource },
        },
        dependencies: storedDependencies,
        graph,
        branchIds: scenariosQuery.data.branchIds,
        existingScenarios: authored,
      },
      {
        onSuccess: ({ scenarios: generated }) => {
          save.mutate({
            pathParams: { ruleId },
            queryParams: undefined,
            body: {
              source: draftSource,
              // A WRITE: a legacy row's packages are cleared, not re-saved.
              dependencies: [],
              validationScenarios: [...authored, ...generated],
            },
          });
        },
      }
    );
  }, [isPendingCoverage, draftSource, ruleId]);
};
